import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { SET_VALUE_OPTIONS } from '@/consts/form';
import { useLocale } from '@/hooks';
import { getSubjectLocaleName } from '@/shared/category/getSubjectLocaleName';
import { useSelectSchoolInfo } from '@/views/build/components/SectionBasicInfo/useSelectSchoolInfo';
import { useBuildInfoContext } from '@/views/build/context';
import { useBuildFormContext } from '@/views/build/form/schema';

import { type TreeMenuItem } from '../../features/category-tree';

const none = 'none';

/**
 * @deprecated features/category-info/hooks/useSubjectState를 사용합니다.
 */
export const useSelectSubject = () => {
  const { subjectTree } = useBuildInfoContext();
  const { t } = useTranslation(['build']);
  const {
    watch,
    setValue,
    formState: { errors },
  } = useBuildFormContext();
  const { locale } = useLocale();

  const { hideSchoolGradeId } = useSelectSchoolInfo();
  const watchSubject = watch('subject');

  const selectedSchoolInfo = new Set(
    [watch('schoolTypeId'), watch('gradeId'), hideSchoolGradeId].filter(
      Boolean,
    ),
  );

  const parentId = (() => {
    if (selectedSchoolInfo.size === 0) {
      return '';
    }
    if (selectedSchoolInfo.size === 1) {
      return Array.from(selectedSchoolInfo)[0];
    }
    if (selectedSchoolInfo.size === 2) {
      return Array.from(selectedSchoolInfo)[1];
    }
    return '';
  })();

  const subjectMenus: TreeMenuItem[] = subjectTree
    .filter(t => t.parentId?.toString() === parentId?.toString())
    .map(c => ({
      value: c.categoryTreeId?.toString(),
      label: getSubjectLocaleName({ ...c, defaultName: c.name, locale }),
      subMenus: [],
      nodes: [c.categoryTreeId?.toString()],
      type: 'SUBJECT',
    }));

  const subjectName = useMemo(() => {
    return String(
      subjectMenus.find(c => c.value.toString() === watchSubject?.toString())
        ?.label ?? '',
    );
  }, [subjectMenus, watchSubject]);

  const subSubjectMenus = useMemo(() => {
    const findSubject = subjectTree.find(
      d => d.categoryTreeId.toString() === watchSubject,
    );
    if (!findSubject) return [];

    const noneMenu: TreeMenuItem = {
      value: none,
      label: t('select.unselect'),
      nodes: [],
      type: 'SUB_SUBJECT',
      subMenus: [],
    };

    const menu: TreeMenuItem[] = findSubject.children.map(c => ({
      value: c.categoryTreeId?.toString(),
      label: getSubjectLocaleName({ ...c, defaultName: c.name, locale }),
      subMenus: [],
      nodes: [c.categoryTreeId?.toString()],
      type: 'SUB_SUBJECT',
    }));

    return [noneMenu].concat(menu);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectTree, t, watchSubject]);

  return {
    selectedSubject: watch('subject'),
    selectedSubSubject: watch('subSubject'),
    subjectName,
    subSubjectName:
      subSubjectMenus.find(d => d.value === watch('subSubject'))?.label ?? null,
    setSubject: (subject: string) => {
      setValue('subSubject', undefined, SET_VALUE_OPTIONS);
      setValue('subject', subject, SET_VALUE_OPTIONS);
    },
    setSubSubject: (value: string) => {
      setValue('subSubject', value, SET_VALUE_OPTIONS);
    },
    subjectMenus,
    subSubjectMenus,
    hasOwnSubjectMenus: subjectMenus.length > 0,
    // 해당 카테고리에 하위 카테고리가 있는지 확인
    hasOwnSubSubjectMenusById: (id?: string) =>
      (
        subjectTree.find(sm => sm.categoryTreeId?.toString() === id?.toString())
          ?.children ?? []
      ).length > 0,
    errorsMessage: errors.subject?.message || '',
    error: Boolean(errors.subject),
  };
};
